/**
 * Registro centralizzato dei testi di aiuto contestuale delle sezioni.
 * Ogni voce = titolo + corpo mostrato nel riquadro "?" (componente HelpHint).
 * Modifica qui i testi: le viste li richiamano con <HelpHint topic="..." />.
 */
import type { ReactNode } from 'react';

export type HelpTopic =
  // Sezione Mail
  | 'feed'
  | 'list'
  | 'search'
  | 'folders'
  | 'sent'
  | 'deletion'
  | 'dashboard'
  | 'kpi'
  // Sezione LinkedIn
  | 'linkedin'
  | 'saved'
  | 'contacts';

type HelpEntry = { title: string; body: ReactNode };

export const HELP: Record<HelpTopic, HelpEntry> = {
  // ----------------------------- MAIL -----------------------------
  feed: {
    title: 'Feed',
    body: (
      <>
        <p>Il flusso unico dei messaggi in arrivo, ordinati dal più recente.</p>
        <p>
          Ogni mail mostra i <b className="text-zinc-200">tag automatici</b> assegnati dall’AI: usali
          come filtro rapido in alto per vedere solo ciò che ti interessa.
        </p>
      </>
    ),
  },
  list: {
    title: 'Posta',
    body: (
      <>
        <p>L’elenco della tua posta con i tag AI e gli strumenti di gestione.</p>
        <p>
          Filtra per <b className="text-zinc-200">tag</b>, seleziona più mail per azioni in blocco
          (spam, sposta, tag) e apri un messaggio per leggerlo e rispondere.
        </p>
      </>
    ),
  },
  search: {
    title: 'Ricerca',
    body: (
      <>
        <p>Cerca tra tutti i tuoi messaggi per mittente, oggetto o testo.</p>
        <p>Combina la ricerca con i tag per restringere subito i risultati.</p>
      </>
    ),
  },
  folders: {
    title: 'Cartelle',
    body: (
      <>
        <p>Le cartelle della casella (IMAP) sincronizzate con il server.</p>
        <p>Seleziona una cartella per vederne i messaggi; le modifiche restano allineate al server.</p>
      </>
    ),
  },
  sent: {
    title: 'Inviati',
    body: <p>I messaggi che hai spedito. Riaprili per rileggerli, inoltrarli o rispondere di nuovo.</p>,
  },
  deletion: {
    title: 'Cestino',
    body: (
      <>
        <p>Le mail eliminate restano qui in un’area di parcheggio prima della rimozione definitiva.</p>
        <p>
          Da qui puoi <b className="text-zinc-200">ripristinare</b> un messaggio o svuotare
          definitivamente il cestino.
        </p>
      </>
    ),
  },
  dashboard: {
    title: 'Dashboard',
    body: (
      <>
        <p>Una panoramica a colpo d’occhio: volumi di posta, tag più frequenti e attività recente.</p>
        <p>Utile per capire come l’AI sta classificando la tua casella.</p>
      </>
    ),
  },
  kpi: {
    title: 'KPI tag',
    body: (
      <>
        <p>I numeri per ciascun tag: quante mail sono state classificate in ogni categoria.</p>
        <p>Ti aiuta a capire quali argomenti pesano di più sulla tua posta.</p>
      </>
    ),
  },
  // --------------------------- LINKEDIN ---------------------------
  linkedin: {
    title: 'Assistente LinkedIn',
    body: (
      <>
        <p>
          L’estensione Chrome salva <b className="text-zinc-200">post, contatti ed engagement</b> da
          LinkedIn direttamente nella webmail.
        </p>
        <p>
          Gli elementi arrivano già riassunti e taggati dall’AI nel tab <b className="text-zinc-200">Salvati</b>;
          i contatti finiscono nel CRM. Non automatizza azioni su LinkedIn (anti-ban).
        </p>
      </>
    ),
  },
  saved: {
    title: 'Salvati',
    body: (
      <>
        <p>Post ed engagement salvati da LinkedIn con l’estensione.</p>
        <p>Ognuno ha riassunto e tag AI: filtra e ritrova rapidamente ciò che ti serve.</p>
      </>
    ),
  },
  contacts: {
    title: 'Contatti (CRM)',
    body: (
      <>
        <p>I contatti salvati da LinkedIn: nome, ruolo, azienda e link al profilo.</p>
        <p>Apri una scheda per vedere note, storico e collegamenti con le tue email.</p>
      </>
    ),
  },
};
